'use client';

import { motion } from 'framer-motion';
import {
	ArrowUpRight,
	BarChart2,
	Clock,
	DollarSign,
	Globe,
	Headphones,
	type LucideIcon,
	MessageSquare,
	Shield,
	Users,
	Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Card, CardContent } from '@/components/ui/card';
import { components } from '@/types/strapi';

const ANIMATION_DURATION = 2000;

const iconMap: Record<string, LucideIcon> = {
	Shield,
	BarChart2,
	Headphones,
	Globe,
	ArrowUpRight,
	Clock,
	DollarSign,
	Zap,
	Users,
	MessageSquare,
};

export const Metric = ({
	value,
	title,
	description,
	iconName,
	backgroundColor,
	gradientStartColor,
	gradientEndColor,
	prefix,
	suffix,
}: components['schemas']['Metric']) => {
	const Icon =
		iconName && iconMap[iconName] ? iconMap[iconName] : MessageSquare;
	const isLoaded = true;
	return (
		<Card className="group relative h-full overflow-hidden border-0 bg-gradient-to-br from-white to-gray-50/50 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
			<CardContent className="p-8">
				<div className="flex flex-col items-center space-y-6 text-center">
					<div
						className={`relative h-20 w-20 ${backgroundColor || 'bg-blue-500'} rotate-3 transform rounded-2xl transition-all duration-300 hover:shadow-lg group-hover:rotate-6 group-hover:scale-110`}>
						<div
							className={`absolute inset-0 bg-gradient-to-br from-${gradientStartColor || 'blue-400'} to-${gradientEndColor || 'blue-600'} rounded-2xl opacity-90 transition-opacity duration-300 group-hover:opacity-100`}
						/>
						<div className="absolute inset-0 flex items-center justify-center text-white">
							<Icon className="h-10 w-10" />
						</div>
					</div>
					<motion.div
						initial={{ scale: 0.5 }}
						animate={{ scale: 1 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="space-y-3">
						<div className="relative">
							<motion.div
								className="bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-5xl font-bold tracking-tight text-transparent"
								animate={isLoaded ? { y: [2, -2, 2] } : {}}
								transition={{
									duration: 3,
									repeat: Infinity,
									ease: 'easeInOut',
								}}>
								{prefix && <span>{prefix}</span>}
								{value.toLocaleString()}
								{suffix && <span>{suffix}</span>}
							</motion.div>
						</div>
						<div className="space-y-2">
							{title && (
								<h3 className="text-lg font-semibold text-gray-900">
									{title}
								</h3>
							)}
							{description && (
								<p className="text-sm leading-relaxed text-gray-500">
									{description}
								</p>
							)}
						</div>
					</motion.div>
				</div>
			</CardContent>
		</Card>
	);
};
